/**
 * 徳島県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 * 9品が農水省「うちの郷土料理」および地理的表示（GI）登録から、3品が徳島県公式「阿波ふうど」から。
 *
 * 形のばらつき。
 *  小さな球（すだち）／平たい板（阿波ういろ）／球（いももち）／平たい楕円（フィッシュカツ）／
 *  串・縦長（でこまわし）／細長いリボン（鳴門わかめ）／長い麺（半田そうめん）／
 *  短く太い麺（祖谷そば）／細長い一尾（ボウゼ寿司）／大きく平たい石（ひらら焼き）。
 *  器を描く例外は、そば米雑炊（椀）と阿波尾鶏鍋（土鍋）とひらら焼き（石）の3つ。
 *  ひらら焼きの石は平たく横に広いので、丸い器にはならない。
 *
 * 他県との重なりをどう避けたか。
 *  - 半田そうめんは、そうめん4品目。農水省が「一般的なそうめんよりも少し太めでコシがあり」と
 *    断定形で書いているので、太さで差別化する。絵でも太さを必ず出す。
 *  - 祖谷そばは、そば4品目。農水省が「つなぎの粉を入れずに作るため麺が切れやすく、太く短くなる」と
 *    書いている。福井のおろしそば、山形の板そば、島根の割子そばとは麺の形そのものが違う。
 *  - 阿波尾鶏は鍋にする。既出の鶏7品はすべて揚げる・焼く・飯で、鍋はひとつもない。
 *  - 鳴門わかめは湯通しした鮮やかな緑のリボン。鳥取の板わかめ、島根の隠岐アラメは乾物なので混ざらない。
 *  - たらいうどん、徳島ラーメン、阿波牛は使わない（うどん・ラーメン・牛の重複回避）。
 *  - 鯛も使わない（広島・香川・熊本で3回使用済み）。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 阿波ういろの「日本三大ういろう」は断定形かどうか未確認。使わない。
 *  - 阿波尾鶏の「地鶏肉出荷量20年連続全国1位」、徳島すだちの「全国の98%」はどちらも
 *    年で変わる。使わない。かわりに「昭和49年に県の花に指定」という変わらない事実を書く。
 *  - でこまわしの名の由来、いももちの由来はどちらも伝聞形。断定しない。
 *
 * 本番画像は public/assets/foods/tokushima/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：阿波ういろ・いももち・ボウゼの姿寿司・あめごのひらら焼き（店で食べられる品に差し替え）
 * - 竹ちくわ：「竹ちくわ発祥の地」は書かない（県の食材サイトの記事にあるが、書き手の個人的な語りの中の記述で、自治体の資料として裏が取れていない）
 * - 竹ちくわ：徳島でちくわを「ちっか」と呼ぶことは書かない（記事の書き手の言い回しで、県全体の呼称として確かめていない）
 * - 鳴門金時の天ぷら（スティック揚げ）：皮の赤紫・身の黄金色は書かない（開いた公式ページで色の記述を確かめられなかった）
 * - 鳴門金時の天ぷら（スティック揚げ）：「スティック揚げ」という形は書かない（出典に記述がない。切り方は店や家庭で違う）
 * - 徳島ラーメン：全国に広めた店の名前は書かない（出典の店紹介にあるが、店の宣伝になる）
 * - 徳島ラーメン：「すき焼き風」は書かない（店紹介の中の表現）
 * - 骨付き阿波尾鶏：「生産量日本一」は書かない（県サイトの商品ページにある販売者のキャッチフレーズで、測った数字の出典を確かめていない）
 * - 骨付き阿波尾鶏：「骨付き阿波尾鶏」という料理の作り方・発祥の店は書かない（店のメニュー名で、公式資料に料理としての記述がない）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { tokushimaSceneryTour } from './tokushimaScenes';
import { TOKUSHIMA_FOOD_HULLS } from './tokushimaFoodShapes.generated';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'sudachi',
    level: 1,
    name: '徳島すだち',
    shortName: 'すだち',
    image: '/assets/foods/tokushima/01_sudachi.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '緑のうちに搾ってこそ、阿波の香り。',
    source: 'https://www.maff.go.jp/j/shokusan/gi_act/register/0129/index.html',
    icon: { label: 'すだち', shape: 'berry', baseColor: '#3a7a28', accentColor: '#c8e078' },
    reward: {
      unlockScore: 200,
      area: '徳島県内全域',
      headline: '濃い緑のうちに収穫する小さな香酸柑橘。焼き魚にも麺にも、ひと搾りで食卓が締まる。',
      tip: '国の地理的表示に「徳島すだち」として登録された果実。万葉の昔から徳島に自生し、昭和49年には県の花にも指定された。',
      links: searchLinks('徳島県', 'すだち'),
    },
  },
  {
    id: 'take-chikuwa',
    level: 2,
    name: '竹ちくわ',
    shortName: '竹ちくわ',
    image: '/assets/foods/tokushima/02_take_chikuwa.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '竹に魚のすり身を巻きつけて焼き上げたちくわ。竹がついたまま売られ、竹を持ってそのままかぶりつける。小松島市の特産品で、かつては徳島の玄関口だった小松島港の定番の土産だった。',
    source:
      'https://awa-food-tokushima.com/archives/7042',
    icon: { label: '竹ちくわ', shape: 'fish', baseColor: '#c98a45', accentColor: '#5f9e3a' },
    reward: {
      unlockScore: 500,
      area: '小松島市',
      headline: '竹を持ってかぶりつくちくわ',
      tip: 'すだちを絞るのが定番。源義経が小松島に上陸したときに味わったとされる。竹を外して切れば煮物や天ぷらにも使える。',
      links: searchLinks('小松島市', '竹ちくわ'),
      official: [{ label: '阿波ふうど「イベントのお供に『竹ちくわ』が活躍する理由」', url: 'https://awa-food-tokushima.com/archives/14876' }],
    },
  },
  {
    id: 'naruto-kintoki-tempura',
    level: 3,
    name: '鳴門金時の天ぷら（スティック揚げ）',
    shortName: '金時の天ぷら',
    image: '/assets/foods/tokushima/03_naruto_kintoki_tempura.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '鳴門市・徳島市・板野郡の砂地の畑で育つさつまいも「なると金時」を、皮ごと天ぷらにする。繊維が少なく煮くずれしにくい。夏に出回る皮のうすい「早掘り」は、天ぷらで皮ごと食べるのがすすめられている。',
    source:
      'https://awa-food-tokushima.com/archives/14066',
    icon: { label: '金時天', shape: 'dumpling', baseColor: '#f1c24a', accentColor: '#9b2f5a' },
    reward: {
      unlockScore: 1000,
      area: '鳴門市／徳島市／板野郡',
      headline: '砂地で育つ芋を皮ごと揚げる',
      tip: '早掘りは4月はじめに植えて約90日で掘り、7〜9月に出回る。10月以降は貯蔵したものが出荷される。',
      links: searchLinks('鳴門市', 'なると金時 天ぷら'),
      official: [{ label: '阿波ふうど「徳島が誇る万能食材、なると金時」', url: 'https://awa-food-tokushima.com/archives/11552' }],
    },
  },
  {
    id: 'fish-katsu',
    level: 4,
    name: 'フィッシュカツ',
    shortName: 'フィッシュカツ',
    image: '/assets/foods/tokushima/04_fish_katsu.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: 'カレー色のすり身を、平たく揚げて。',
    source: 'https://awa-food-tokushima.com/archives/14888',
    icon: { label: 'フィッシュカツ', shape: 'dumpling', baseColor: '#d8a040', accentColor: '#e8cc60' },
    reward: {
      unlockScore: 1800,
      area: '徳島市ほか県内全域',
      headline: '魚のすり身にカレー粉を混ぜ、パン粉をつけて揚げたカツ。徳島では「天ぷら」とも呼ばれる。',
      tip: '徳島のスーパーに当たり前に並ぶ惣菜。お好み焼きにのせたり、マヨネーズと醤油をかけて食べたりする。',
      links: searchLinks('徳島県', 'フィッシュカツ'),
    },
  },
  {
    id: 'dekomawashi',
    level: 5,
    name: 'でこまわし',
    shortName: 'でこまわし',
    image: '/assets/foods/tokushima/05_dekomawashi.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '串をくるくる、木偶が首を回すように。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/44_3_tokushima.html',
    icon: { label: 'でこまわし', shape: 'dumpling', baseColor: '#8a6030', accentColor: '#c8b070' },
    reward: {
      unlockScore: 3000,
      area: '三好市祖谷',
      headline: 'こんにゃく・豆腐・じゃがいも・里芋・そば団子を串に刺し、味噌だれを塗って囲炉裏で焼く。',
      tip: '串を回しながら焼く姿が、人形浄瑠璃の木偶が頭を回す様子に似ていることから名が付いたといわれる。いちばん下のこんにゃくがストッパーになる。',
      links: searchLinks('三好市', 'でこまわし'),
    },
  },
  {
    id: 'naruto-wakame',
    level: 6,
    name: '鳴門わかめ',
    shortName: '鳴門わかめ',
    image: '/assets/foods/tokushima/06_naruto_wakame.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '湯にくぐらせた瞬間、緑がひらく。',
    source: 'https://awa-food-tokushima.com/archives/8132',
    icon: { label: '鳴門わかめ', shape: 'fish', baseColor: '#2a6a3a', accentColor: '#8ac878' },
    reward: {
      unlockScore: 4500,
      area: '鳴門市',
      headline: '湯にくぐらせると茶褐色から鮮やかな緑へ。鳴門の潮で育ち、シャキシャキした歯ごたえ。',
      tip: '新わかめの時期は1月から3月。しゃぶしゃぶやわかめご飯で、磯の香りと歯ごたえを味わう。',
      links: searchLinks('鳴門市', '鳴門わかめ'),
    },
  },
  {
    id: 'handa-somen',
    level: 7,
    name: '半田そうめん',
    shortName: '半田そうめん',
    image: '/assets/foods/tokushima/07_handa_somen.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: 'そうめんなのに、この噛みごたえ。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/44_4_tokushima.html',
    icon: { label: '半田そうめん', shape: 'ramen', baseColor: '#f0eee6', accentColor: '#8ab04a' },
    reward: {
      unlockScore: 6500,
      area: 'つるぎ町半田',
      headline: '一般的なそうめんより少し太く、はっきりしたコシ。食べ応えのある徳島・半田の手延べ麺。',
      tip: '江戸時代の中ごろ、船乗りが麺づくりの技術を持ち込んだのが始まりと伝わる。太めでコシがあり、食べ応えがあるのが半田の麺の特徴。',
      links: searchLinks('つるぎ町', '半田そうめん'),
    },
  },
  {
    id: 'iya-soba',
    level: 8,
    name: '祖谷そば',
    shortName: '祖谷そば',
    image: '/assets/foods/tokushima/08_iya_soba.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: 'つなぎなし、太くて短い山のそば。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/44_5_tokushima.html',
    icon: { label: '祖谷そば', shape: 'ramen', baseColor: '#8a7a60', accentColor: '#4a4030' },
    reward: {
      unlockScore: 9000,
      area: '三好市祖谷',
      headline: 'つなぎを使わないので麺は太く短く、ぽきぽき切れる。そば粉の香りがそのまま立つ山のそば。',
      tip: '米が育ちにくい山で、栽培期間の短いそばが主食になった。つなぎを入れないため麺が短く切れ、「そばきり」とも呼ばれる。',
      links: searchLinks('三好市', '祖谷そば'),
    },
  },
  {
    id: 'sobagome-zosui',
    level: 9,
    name: 'そば米雑炊',
    shortName: 'そば米雑炊',
    image: '/assets/foods/tokushima/09_sobagome_zosui.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '粉にはしない。実のまま、椀の中へ。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/44_1_tokushima.html',
    icon: { label: 'そば米雑炊', shape: 'pot', baseColor: '#c8a870', accentColor: '#8ab04a' },
    reward: {
      unlockScore: 12000,
      area: '三好市祖谷ほか県内全域',
      headline: 'そばの実を塩ゆでして殻をむき、乾かした「そば米」を、だしで炊いた温かい雑炊。',
      tip: 'そば米は煮ると四倍以上にふくらむ。粉にせず実のまま食べる文化は珍しく、正月に平家の人々が食べたとも伝わる。',
      links: searchLinks('三好市', 'そば米雑炊'),
    },
  },
  {
    id: 'tokushima-ramen',
    level: 10,
    name: '徳島ラーメン',
    shortName: '徳島ラーメン',
    image: '/assets/foods/tokushima/10_tokushima_ramen.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: 'スープは甘辛い豚骨醤油の「茶系」、鶏ガラや野菜も使うまろやかな「黄系」、あっさりの「白系」の3つの系統に分かれる。具はメンマ・ねぎ・もやしに、甘辛く味をつけた豚バラ肉と、スープをまろやかにする生卵をのせるのが主流。',
    source:
      'https://www.awanavi.jp/archives/topics/20866',
    icon: { label: '徳島ラーメン', shape: 'ramen', baseColor: '#5a3420', accentColor: '#f2a21e' },
    reward: {
      unlockScore: 16000,
      area: '徳島市',
      headline: '甘辛い豚バラと生卵をのせる',
      tip: '戦後の屋台で出されていた白く濁ったスープのラーメンがルーツとされる。継ぎ足しのたれで仕上げる豚バラ肉は、ご飯にのせて食べるのもすすめられている。',
      links: searchLinks('徳島市', '徳島ラーメン'),
      official: [{ label: '阿波ナビ「徳島のラーメン＆中華そばガイド」', url: 'https://www.awanavi.jp/archives/topics/65668' }],
    },
  },
  {
    id: 'honetsuki-awaodori',
    level: 11,
    name: '骨付き阿波尾鶏',
    shortName: '骨付き阿波尾鶏',
    image: '/assets/foods/tokushima/11_honetsuki_awaodori.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '阿波尾鶏は徳島の地鶏。県の西部や南部で、地鶏肉の日本農林規格にもとづき75日以上かけて育てる。高たんぱく・低脂肪でうま味が濃く、やや赤みを帯びた肉色と、ほどよい歯ごたえがある。',
    source:
      'https://awa-food-tokushima.com/archives/16471',
    icon: { label: '骨付き鶏', shape: 'feast', baseColor: '#b8702e', accentColor: '#f3e1c0' },
    reward: {
      unlockScore: 22000,
      area: '徳島県全域',
      headline: '75日以上かけて育てる地鶏',
      tip: '名前は阿波おどりにちなむ。阿波おどりの前日にあたる8月8日は「阿波尾鶏の日」として記念日に登録されている。',
      links: searchLinks('徳島市', '骨付き阿波尾鶏'),
      official: [{ label: '徳島県阿波尾鶏ブランド確立対策協議会', url: 'https://www.tokutori.org/tori/' }],
    },
  },
  {
    id: 'awaodori-nabe',
    level: 12,
    name: '阿波尾鶏の鍋',
    shortName: '阿波尾鶏鍋',
    image: '/assets/foods/tokushima/12_awaodori_nabe.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '軍鶏の血を引く、噛みしめるほど濃い。',
    source: 'https://www.maff.go.jp/j/shokusan/gi_act/register/0127/index.html',
    icon: { label: '阿波尾鶏鍋', shape: 'hotpot', baseColor: '#e8e0d0', accentColor: '#c84a3a' },
    reward: {
      unlockScore: 30000,
      area: '徳島県内（県西部・南部が中心）',
      headline: '県在来の赤笹系軍鶏を親にもつ地鶏。鍋にすると、うま味の濃い出汁が土鍋いっぱいに出る。',
      tip: '県在来の赤笹系軍鶏と肉用種鶏を掛け合わせ、およそ十年かけて生まれた地鶏。名前は阿波おどりにちなむ。やや赤みを帯びた肉は低脂肪でうま味が濃い。',
      links: searchLinks('徳島県', '阿波尾鶏'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const tokushimaFoodStage: StageDefinition = {
  regionId: 'tokushima',
  regionName: '徳島県',
  mode: 'food',
  title: '徳島めしマージ',
  theme: {
    primaryColor: '#2a5a4a',
    secondaryColor: '#d8c058',
    accentColor: '#e8d880',
    backgroundColor: '#121a18',
  },
  /** 背景ツアー「徳島めぐり」。軸は季節と時間。春の朝の霊山寺から、冬の落合集落へ。 */
  scenery: {
    tour: tokushimaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, TOKUSHIMA_FOOD_HULLS),
};
